import { PageHeader , Button , Card, CardContent , Skeleton ,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@roberttamas/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Download, Plus } from "@/components/icons";
import { LocaleFlag } from "@/components/locale-flag";
import { getTranslations, listVariants } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { formatLocaleLabel } from "@/lib/locales";
import {
  buildTranslationDetailPath,
  exportTranslationsJson,
} from "@/lib/translations";
import type { ListVariantsResp } from "@/lib/types";

export function TranslationsListPage() {
  const { t } = useTranslation();
  const query = useQuery<ListVariantsResp>({ queryKey: ["variants"], queryFn: listVariants });

  const downloadMutation = useMutation({
    mutationFn: (variant: string) => getTranslations({ variant }),
    onSuccess: (resp) => {
      exportTranslationsJson(resp.variant, resp.keys);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("translations.eyebrow")}
        title={t("translations.title")}
        subtitle={t("translations.subtitle")}
        actions={
          <Button asChild>
            <Link to="/translations/new">
              <Plus className="h-4 w-4" />
              {t("translations.newTranslation")}
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("translations.colLanguage")}</TableHead>
                <TableHead>{t("translations.colLocaleTag")}</TableHead>
                <TableHead className="text-right">{t("translations.colActions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 3 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {query.isError && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <div className="flex items-center justify-between gap-4 py-6 text-sm">
                      <span className="text-destructive">{t("translations.loadFailed")}</span>
                      <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
                        {t("common.retry")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {query.data && query.data.variants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <div className="flex flex-col items-center gap-3 py-12 text-center">
                      <p className="text-sm text-muted-foreground">{t("translations.empty")}</p>
                      <Button asChild size="sm">
                        <Link to="/translations/new">{t("translations.createFirst")}</Link>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {query.data?.variants.map((variant) => {
                const downloading =
                  downloadMutation.isPending && downloadMutation.variables === variant;

                return (
                  <TableRow key={variant}>
                    <TableCell className="font-medium">
                      <Link
                        to={buildTranslationDetailPath(variant)}
                        className="flex items-center gap-2.5 hover:underline"
                      >
                        <LocaleFlag tag={variant} />
                        <span>{formatLocaleLabel(variant)}</span>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                        {variant}
                      </code>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={downloadMutation.isPending}
                        onClick={() => downloadMutation.mutate(variant)}
                      >
                        <Download className="h-4 w-4" />
                        {downloading ? t("translations.downloading") : t("translations.download")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}